import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { AppProvider, useApp } from "@/context/AppContext";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import { AdminI18nProvider } from "@/i18n/admin_i18n";
import { Toaster } from "sonner";

// Heavy or rarely-visited routes are code-split so the homepage bundle stays
// small. Home + Shop remain eager because they're the two most-visited entry
// points and both are already needed for the first paint / navigation.
const ProductDetail    = lazy(() => import("@/pages/ProductDetail"));
const Checkout         = lazy(() => import("@/pages/Checkout"));
const PaymentSuccess   = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel    = lazy(() => import("@/pages/PaymentCancel"));
const About            = lazy(() => import("@/pages/About"));
const Contact          = lazy(() => import("@/pages/Contact"));
const Auth             = lazy(() => import("@/pages/Auth"));
const AuthCallback     = lazy(() => import("@/pages/AuthCallback"));
const Account          = lazy(() => import("@/pages/Account"));
const ForgotPassword   = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword    = lazy(() => import("@/pages/ResetPassword"));
const Wishlist         = lazy(() => import("@/pages/Wishlist"));
const OrderTracking    = lazy(() => import("@/pages/OrderTracking"));
const Consultation     = lazy(() => import("@/pages/Consultation"));
const LegalPage        = lazy(() => import("@/pages/LegalPage"));
const Booking          = lazy(() => import("@/pages/Booking"));
const BookingReview    = lazy(() => import("@/pages/BookingReview"));
const BookingSchedule  = lazy(() => import("@/pages/BookingSchedule"));
const BookingCustomer  = lazy(() => import("@/pages/BookingCustomer"));
const BookingSuccess   = lazy(() => import("@/pages/BookingSuccess"));
const AdminLogin       = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard   = lazy(() => import("@/pages/AdminDashboard"));

const PageLoading = () => (
  <div className="min-h-[60vh] grid place-items-center bg-[#0A0A0A]">
    <div className="w-8 h-8 border-2 border-white/10 border-t-gold rounded-full animate-spin" aria-label="loading" />
  </div>
);

const AdminLoading = () => (
  <div className="min-h-screen grid place-items-center bg-[#0A0A0A]">
    <div className="w-8 h-8 border-2 border-white/10 border-t-gold rounded-full animate-spin" aria-label="loading" />
  </div>
);

function AdminGate({ children }) {
  const { user, token, hydrating } = useApp();
  if (token && hydrating) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0A0A0A]" data-testid="admin-hydrating">
        <div className="w-8 h-8 border-2 border-white/10 border-t-gold rounded-full animate-spin" aria-label="loading" />
      </div>
    );
  }
  if (!token || !user || !["admin","super_admin"].includes(user.role)) {
    return <Navigate to="/admin/login" replace />;
  }
  return children;
}

// Small wrapper that suspends any lazy route with the standard spinner so
// each <Route element={...}> stays terse.
const S = (el) => <Suspense fallback={<PageLoading />}>{el}</Suspense>;

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <Toaster position="top-center" theme="dark" richColors />
        <Routes>
          <Route path="/admin/login" element={<AdminI18nProvider><Suspense fallback={<AdminLoading />}><AdminLogin /></Suspense></AdminI18nProvider>} />
          <Route path="/admin" element={<AdminI18nProvider><AdminGate><Suspense fallback={<AdminLoading />}><AdminDashboard /></Suspense></AdminGate></AdminI18nProvider>} />
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/shop/:category" element={<Shop />} />
            <Route path="/product/:slug" element={S(<ProductDetail />)} />
            <Route path="/checkout" element={S(<Checkout />)} />
            <Route path="/payment/success" element={S(<PaymentSuccess />)} />
            <Route path="/payment/cancel" element={S(<PaymentCancel />)} />
            <Route path="/about" element={S(<About />)} />
            <Route path="/contact" element={S(<Contact />)} />
            <Route path="/consultation" element={S(<Consultation />)} />
            <Route path="/booking" element={S(<Booking />)} />
            <Route path="/booking/review" element={S(<BookingReview />)} />
            <Route path="/booking/schedule" element={S(<BookingSchedule />)} />
            <Route path="/booking/customer" element={S(<BookingCustomer />)} />
            <Route path="/booking/success" element={S(<BookingSuccess />)} />
            <Route path="/order-tracking" element={S(<OrderTracking />)} />
            <Route path="/wishlist" element={S(<Wishlist />)} />
            <Route path="/auth" element={S(<Auth />)} />
            <Route path="/auth/callback" element={S(<AuthCallback />)} />
            <Route path="/forgot-password" element={S(<ForgotPassword />)} />
            <Route path="/reset-password" element={S(<ResetPassword />)} />
            <Route path="/account" element={S(<Account />)} />
            <Route path="/impressum" element={S(<LegalPage pageKey="impressum" />)} />
            <Route path="/datenschutz" element={S(<LegalPage pageKey="datenschutz" />)} />
            <Route path="/agb" element={S(<LegalPage pageKey="agb" />)} />
            <Route path="/cookies" element={S(<LegalPage pageKey="cookies" />)} />
            <Route path="/payments" element={S(<LegalPage pageKey="payments" />)} />
            <Route path="/faq" element={S(<LegalPage pageKey="faq" />)} />
            <Route path="/shipping" element={S(<LegalPage pageKey="shipping" />)} />
            <Route path="/returns" element={S(<LegalPage pageKey="returns" />)} />
            <Route path="*" element={<Navigate to="/" />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
